import styled from 'styled-components';
import colors from 'assets/theme/base/colors';
import { UndoRounded } from '@mui/icons-material';

export const StyledTabsContainer = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'orientation',
}) <{ orientation: 'horizontal' | 'vertical' }>`
  display: flex;
  flex-direction: ${({ orientation }) => orientation === 'vertical' ? 'row' : 'column'};
  width: 100%;
`;

export const StyledTabList = styled.div.withConfig({
  shouldForwardProp: (prop: string) => !['orientation', 'variant'].includes(prop),
}) <{
  orientation: 'horizontal' | 'vertical';
  variant: 'default' | 'underline';
}>`
  display: flex;
  flex-direction: ${({ orientation }) => orientation === 'vertical' ? 'column' : 'row'};
  gap: 0;
  min-width: ${({ orientation }) => orientation === 'vertical' ? '12.5rem' : 'auto'};

  /* Underline variant borders */
  border-bottom: ${({ variant, orientation }) =>
    variant === 'underline' && orientation === 'horizontal' ? '1px solid' : 'none'};
  border-right: ${({ variant, orientation }) =>
    variant === 'underline' && orientation === 'vertical' ? '1px solid' : 'none'};

  /* Default variant outlined container */
  width: ${({ variant }) => variant === 'default' ? '100%' : 'auto'};
  border: ${({ variant }) => variant === 'default' ? '1px solid' : 'none'};
  border-radius: ${({ variant }) => variant === 'default' ? '0.25rem' : '0'};
  padding: ${({ variant }) => variant === 'default' ? '0.375rem' : '0'};
  ${''/* Even distribution across width */}
  ${({ variant }) => variant === 'default' ? 'justify-content: space-between;' : ''}

  border-color: ${({ variant }) => variant === 'default' ? `${colors.secondary.main}80` : colors.grey[300]};
  margin-bottom: ${({ variant, orientation }) =>
    variant === 'underline' && orientation === 'horizontal' ? '1rem' : '0'};
  margin-right: ${({ variant, orientation }) =>
    variant === 'underline' && orientation === 'vertical' ? '1rem' : '0'};
`;

export const StyledTabButton = styled.button.withConfig({
  shouldForwardProp: (prop: string) => !['isActive', 'disabled', 'orientation', 'variant', 'ariaLabel'].includes(prop),
}) <{
  isActive: boolean;
  disabled: boolean;
  orientation: 'horizontal' | 'vertical';
  variant: 'default' | 'underline';
  ariaLabel?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  transition: all 0.2s ease-in-out;
  outline: none;
  border: none;
  background: transparent;
  position: relative;
  white-space: nowrap;
  flex: 1 1 auto;

  ${({ variant, orientation, isActive, disabled }) => {
    if (variant === 'underline') {
      return `
        border-bottom: ${orientation === 'horizontal' ? '2px solid' : 'none'};
        border-right: ${orientation === 'vertical' ? '2px solid' : 'none'};
        border-color: ${isActive ? colors.primary.main : colors.grey[200]};
        color: ${isActive ? colors.primary.main : colors.grey[500]};
        opacity: ${isActive ? 1 : 0.6};
        
        &:hover {
          ${!disabled && !isActive ? `
            color: ${colors.grey[600]};
            opacity: 0.8;
            border-color: ${colors.grey[300]};
          ` : ''}
        }
        
        &:focus {
          color: ${colors.primary.main};
          opacity: 1;
          border-color: ${colors.primary.main};
        }
      `;
    }

    // Default variant: outlined container with pill-like active state
    return `
      border-radius: 0.25rem;
      background-color: ${isActive ? colors.secondary.main : 'transparent'};
      color: ${isActive ? '#ffffff' : "#22459fcc"};
      padding: 0.625rem 0.875rem;
      border: none;
      
      &:hover {
        ${!disabled && !isActive ? `
          background-color: transparent;
          color: ${colors.text.main};
        ` : ''}
      }
      
      &:focus {
        outline: none;
      }
    `;
  }}
`;

export const StyledTabTypography = styled.span.withConfig({
  shouldForwardProp: (prop: string) => !['isActive', 'tabVariant'].includes(prop),
}) <{
  isActive: boolean;
  tabVariant: 'default' | 'underline';
}>`
  text-transform: none;
  font-size: 1.1rem;
  font-weight: ${({ isActive, tabVariant }) =>
    isActive ? 'bold' :
      tabVariant === 'default' ? '600' : 'normal'};
  color: ${({ isActive, tabVariant }) =>
    tabVariant === 'default'
      ? (isActive ? '#ffffff' : colors.secondary.main)
      : (isActive
        ? colors.secondary.main
        : colors.grey[600])};
  opacity: ${({ tabVariant, isActive }) =>
    tabVariant === 'underline' ? (isActive ? 1 : 0.6) : 1};

  text-decoration: ${({ isActive }) => isActive ? 'none' : 'underline'};
  text-decoration-color: ${({ isActive }) => isActive ? 'inherit' : colors.secondary.main};
`;

export const StyledTabPanel = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'isActive',
}) <{ isActive: boolean }>`
  display: ${({ isActive }) => isActive ? 'block' : 'none'};
  outline: none;
`;

export const StyledTabPanels = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'orientation',
}) <{ orientation: 'horizontal' | 'vertical' }>`
  flex: 1;
  min-height: 12.5rem;
  padding-left: ${({ orientation }) => orientation === 'vertical' ? '1rem' : '0'};
  padding-top: ${({ orientation }) => orientation === 'horizontal' ? '1rem' : '0'};
`;